export enum ConnectionState {
    CONNECTING = "connecting",
    CONNECTED = "connected",
    DISCONNECTING = "disconnecting",
    DISCONNECTED = "disconnected",
    RECONNECTING = "reconnecting",
    FAILED = "failed",
    /** The server accepted the TCP connection but never sent the HTTP upgrade response */
    BACKEND_UNRESPONSIVE = "backend_unresponsive",
}

export interface RobustWebSocketConfig {
    // Connection
    url: string;

    // Reconnection
    reconnect?: boolean;
    maxReconnectAttempts?: number;
    reconnectInterval?: number;
    reconnectDecay?: number;
    maxReconnectInterval?: number;

    // Heartbeat
    heartbeatInterval?: number;
    heartbeatTimeout?: number;
    clientHeartbeat?: boolean;

    // Callbacks
    onMessage: (data: string) => void;
    onStateChange: (state: ConnectionState, event?: Event) => void;
    onReconnect?: () => void;
    onError?: (error: Event | Error) => void;
}

export interface WebSocketStats {
    reconnectCount: number;
    messagesSent: number;
    messagesReceived: number;
    lastMessageTime: number;
}

export class RobustWebSocket {
    private config: Required<RobustWebSocketConfig>;
    private socket: WebSocket | null = null;
    private state: ConnectionState = ConnectionState.DISCONNECTED;
    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private clientHeartbeatTimer: ReturnType<typeof setTimeout> | null = null;
    private clientHeartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
    private shouldReconnect = true;
    private stats: WebSocketStats = {
        reconnectCount: 0,
        messagesSent: 0,
        messagesReceived: 0,
        lastMessageTime: 0,
    };
    private pendingPingTimestamp: number | null = null;

    constructor(config: RobustWebSocketConfig) {
        this.config = {
            reconnect: true,
            maxReconnectAttempts: Infinity,
            reconnectInterval: 1000,
            reconnectDecay: 1.5,
            maxReconnectInterval: 30_000,
            heartbeatInterval: 30_000,
            heartbeatTimeout: 5_000,
            clientHeartbeat: true,
            onError: () => { },
            onReconnect: () => { },
            ...config,
        };

        this._connect();
    }

    // ---- Connection lifecycle -------------------------------------------
    private _connect(): void {
        // Prevent creating a new socket while an existing one is still opening/closing
        if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.CLOSING)) {
            return;
        }

        this._changeState(ConnectionState.CONNECTING);

        try {
            this.socket = new WebSocket(this.config.url);
            this._setupSocketHandlers();
        } catch (err) {
            // Synchronous errors from `new WebSocket` (invalid URL, etc.)
            this.config.onError?.(err as Error);
            this._changeState(ConnectionState.FAILED);
        }
    }

    private _setupSocketHandlers(): void {
        if (!this.socket) return;

        // Ensure any previous reconnect timer doesn't linger once we open
        this.socket.onopen = (ev) => {
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
            this.reconnectAttempts = 0;
            this._changeState(ConnectionState.CONNECTED, ev);
            this._startHeartbeat();

            if (this.stats.reconnectCount > 0) {
                this.config.onReconnect?.();
            }
        };

        this.socket.onmessage = (ev) => {
            const data = ev.data as string;
            this.stats.lastMessageTime = Date.now();
            this.stats.messagesReceived++;

            // heartbeat handling
            if (data.startsWith("pong")) {
                this._handlePongReceived();
                return;
            }
            if (data.startsWith("ping")) {
                this._sendImmediate("pong");
                return;
            }

            // application message
            this.config.onMessage(data);
        };

        this.socket.onerror = (ev) => {
            this.config.onError?.(ev);
            // Errors are usually followed by onclose – we just forward them.
        };

        this.socket.onclose = (ev) => {
            this._stopHeartbeat();

            // Clean up socket reference to prevent stale references
            this.socket = null;

            // Detect backend never upgraded the connection (no onopen fired)
            if (
                this.state === ConnectionState.CONNECTING &&
                ev.code === 1006 && // abnormal closure
                !ev.wasClean
            ) {
                this._changeState(ConnectionState.BACKEND_UNRESPONSIVE, ev);
            }

            if (this.shouldReconnect && this.config.reconnect) {
                this._attemptReconnect();
            } else {
                this._changeState(ConnectionState.DISCONNECTED, ev);
            }
        };
    }

    private _changeState(newState: ConnectionState, event?: Event): void {
        if (this.state !== newState) {
            this.state = newState;
            this.config.onStateChange(newState, event);
        }
    }

    // ---- Heartbeat (ping/pong) – the *only* staleness detector ----------
    private _startHeartbeat(): void {
        this.stats.lastMessageTime = Date.now();

        if (this.config.clientHeartbeat) {
            this._stopClientHeartbeat();

            this.clientHeartbeatTimer = setInterval(() => {
                this._sendPing();
            }, this.config.heartbeatInterval);
        }
    }

    private _sendPing(): void {
        if (this.pendingPingTimestamp !== null) return; // already waiting

        // Don't send ping if not connected
        if (this.state !== ConnectionState.CONNECTED) return;

        const sent = this._sendImmediate("ping");
        if (!sent) return; // Socket not ready, skip this ping

        this.pendingPingTimestamp = Date.now();

        this.clientHeartbeatTimeoutTimer = setTimeout(() => {
            this.pendingPingTimestamp = null;
            this._handleStaleConnection();
        }, this.config.heartbeatTimeout);
    }

    private _handlePongReceived(): void {
        if (this.clientHeartbeatTimeoutTimer) {
            clearTimeout(this.clientHeartbeatTimeoutTimer);
            this.clientHeartbeatTimeoutTimer = null;
        }
        this.pendingPingTimestamp = null;
    }

    private _handleStaleConnection(): void {
        // No pong → connection is dead; close forces onclose → reconnect
        // include a code and reason so it's obvious why we closed
        try {
            this.socket?.close(4000, "heartbeat timeout");
        } catch {
            // ignore
            this.socket?.close();
        }
    }

    private _stopClientHeartbeat(): void {
        if (this.clientHeartbeatTimer) {
            clearInterval(this.clientHeartbeatTimer);
            this.clientHeartbeatTimer = null;
        }
        if (this.clientHeartbeatTimeoutTimer) {
            clearTimeout(this.clientHeartbeatTimeoutTimer);
            this.clientHeartbeatTimeoutTimer = null;
        }
        this.pendingPingTimestamp = null;
    }

    private _stopHeartbeat(): void {
        this._stopClientHeartbeat();
    }

    // ---- Reconnection logic ---------------------------------------------
    private _getReconnectDelay(): number {
        const { reconnectInterval, reconnectDecay, maxReconnectInterval } = this.config;
        const attempt = this.reconnectAttempts;
        return Math.min(
            reconnectInterval * Math.pow(reconnectDecay, attempt - 1),
            maxReconnectInterval
        );
    }

    private _attemptReconnect(): void {
        if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
            this._changeState(ConnectionState.FAILED);
            return;
        }

        // prevent scheduling multiple timers
        if (this.reconnectTimer) return;

        this._changeState(ConnectionState.RECONNECTING);
        this.reconnectAttempts++;
        this.stats.reconnectCount++;

        const delay = this._getReconnectDelay();
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this._connect();
        }, delay);
    }

    // ---- Public API -----------------------------------------------------
    private _sendImmediate(data: string): boolean {
        if (this.socket?.readyState === WebSocket.OPEN) {
            try {
                this.socket.send(data);
                this.stats.messagesSent++;
                return true;
            } catch {
                // If send throws, close socket to trigger onclose/reconnect flow
                try { this.socket?.close(); } catch { /* ignore */ }
                return false;
            }
        }
        return false;
    }

    public send(data: string): boolean {
        return this.state === ConnectionState.CONNECTED ? this._sendImmediate(data) : false;
    }

    public close(code?: number, reason?: string): void {
        this.shouldReconnect = false;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        this._stopHeartbeat();

        if (this.socket) {
            this._changeState(ConnectionState.DISCONNECTING);
            this.socket.close(code, reason);
        }
    }

    /** Manual reconnect – does *not* reset attempt counters */
    public reconnect(): void {
        if ([ConnectionState.FAILED, ConnectionState.DISCONNECTED, ConnectionState.BACKEND_UNRESPONSIVE].includes(this.state)) {
            this.shouldReconnect = true;
            this._connect();
        }
    }

    public getState(): ConnectionState {
        return this.state;
    }

    public isConnected(): boolean {
        return this.state === ConnectionState.CONNECTED;
    }

    public getStats(): WebSocketStats {
        return { ...this.stats };
    }
}